import { useContext } from "react";
import { AdFormat, WBEAdFormatOption } from "../api/contracts/site-config";
import { AdFormatContext } from "../contexts/adFormatContext";
import { useConfiguration } from "../contexts/ConfigurationProvider";

/**
 * Which ad format the WBE is currently presenting.
 *
 * Callers ask "which format am I showing", never "what kind of site is this" —
 * the two were the same question only while the answer could not change.
 *
 * Falls back to the site's own module when no AdFormatProvider is mounted, so
 * a component rendered outside it (tests, embeds) behaves exactly as it did
 * before formats became selectable.
 */
export const useAdFormat = (): {
  format: AdFormat | undefined;
  isActivity: boolean;
  isPackage: boolean;
  isOffer: boolean;
  /** Whether the site offers packages as a format of their own. When it does,
   *  the lodging grid stops carrying package cards — they have a tab now. */
  offersPackages: boolean;
  options: WBEAdFormatOption[];
  selectFormat: (module: AdFormat) => void;
} => {
  const { configuration } = useConfiguration();
  const context = useContext(AdFormatContext);

  const format = context?.format ?? configuration?.module;
  const options = context?.options ?? [];

  return {
    format,
    isActivity: format === "activity",
    isPackage: format === "package",
    isOffer: format === "offer",
    offersPackages: options.some((option) => option.module === "package"),
    options,
    selectFormat: context?.selectFormat ?? (() => {}),
  };
};

/**
 * Whether the guest has a format to choose between. A single format is not a
 * choice, so the switcher renders nothing — callers that lay out around it
 * need the same answer before they reserve space for it.
 */
export const useHasAdFormatChoice = (): boolean => useAdFormat().options.length > 1;
